import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { useMutation } from '@tanstack/react-query'
import { AnimatePresence } from 'motion/react'
import { parseAsInteger, useQueryState } from 'nuqs'
import { use, useEffect, useState } from 'react'
import { Dialog } from '~/components/dialog'
import { GrantPermissionDialog } from '~/components/grant-permission-dialog'
import { LoadingOverlay } from '~/components/loading-overlay'
import { SubscribedView } from '~/components/subscribed-view'
import { useChinaAndroidTrialMutation } from '~/hooks/use-china-android-trial-mutation'
import {
  pageVisibleOnce,
  regionReady,
  serviceLevelStatusReady,
} from '~/loading'
import { enterFrom, platform } from '~/params'
import { ComparisonPage } from './components/comparison-page'
import { FeaturesPage } from './components/features-page'
import { Layout } from './components/layout'
import { getFeaturesPageVariant } from './lib/features-page-variant'
import { MainPage } from './components/main-page'
import { ReminderStep } from './components/reminder-step'
import { TrialPage } from './components/trial-page'
import { useTrialMutation } from './hooks/use-trial-mutation'
import { runRestore } from './lib/restore'

const PAGE_COUNT = 4

export default function MultiPage() {
  const region = use(regionReady.promise)
  const serviceLevelStatus = use(serviceLevelStatusReady.promise)
  const shouldShowFeaturesPage = checkShouldShowFeaturesPage({
    isProUser: serviceLevelStatus.is_premium,
  })

  const [pageIndex, setPageIndex] = useQueryState(
    'pageIndex',
    parseAsInteger.withDefault(0),
  )
  const nextPage = () => {
    setPageIndex((index) => {
      if (index < PAGE_COUNT - 1) {
        return index + 1
      }
      return index
    })
  }
  const isChinaAndroid = platform === 'china_android'

  const [isSubscribedViewOpen, setIsSubscribedViewOpen] = useState(false)

  const trialMutation = useTrialMutation({
    onSuccess: () => {
      setIsSubscribedViewOpen(true)
    },
  })

  const restoreMutation = useMutation({ mutationFn: runRestore })

  const chinaMutation = useChinaAndroidTrialMutation({
    onSuccess: async () => {
      native.general.logEvent({
        event_name: 'product_sku_purchased_success',
        event_params: { sku_id: 'cna_plus_trial_7' },
      })
      native.general.logEvent({
        event_name: 'plus_trial_success',
        event_params: { sku_id: 'cna_plus_trial_7' },
      })
      setIsSubscribedViewOpen(true)
    },
  })
  const handleChinaAndroidTrial = async () => {
    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        type: 'btn',
        app_action: 'click',
        component_name: 'trial_paywall_trial',
      },
    })
    await chinaMutation.mutateAsync().catch(() => {
      // error is handled in mutation
    })
  }

  useEffect(() => {
    pageVisibleOnce.promise.then(() => {
      const { is_premium } = serviceLevelStatus
      switch (pageIndex) {
        case 0:
          if (!shouldShowFeaturesPage) {
            native.general.logEvent({
              event_name: 'page_view',
              event_params: {
                page_name: is_premium
                  ? 'trial_paywall_intro_premium'
                  : 'trial_paywall_intro_free',
              },
            })
          }
          break
        case 1:
          native.general.logEvent({
            event_name: 'page_view',
            event_params: {
              page_name: is_premium
                ? 'trial_paywall_compare_premium'
                : 'trial_paywall_compare_free',
            },
          })
          break
        // Page 3 (case 2) logs its own page_view from ReminderStep: the name
        // depends on the experiment arm, which only that step waits for.
        case 3:
          native.general.logEvent({
            event_name: 'page_view',
            event_params: {
              page_name: is_premium
                ? 'trial_paywall_plan_premium'
                : 'trial_paywall_plan_free',
            },
          })
          break
      }
    })
  }, [pageIndex])

  return (
    <>
      <Layout
        className={cn(
          'h-full overflow-y-hidden',
          shouldShowFeaturesPage &&
            'bg-[linear-gradient(192deg,_#007A6E_0%,_#001721_56%)]',
        )}
        hideBgOverlay={shouldShowFeaturesPage}
      >
        <AnimatePresence mode="wait">
          {pageIndex === 0 &&
            (shouldShowFeaturesPage ? (
              <FeaturesPage
                next={nextPage}
                key="features"
                variant={getFeaturesPageVariant({
                  enterFrom,
                  isProUser: serviceLevelStatus.is_premium,
                  isChinaRegion: region?.toLowerCase() === 'cn',
                })}
              />
            ) : (
              <MainPage next={nextPage} key="main" />
            ))}
          {pageIndex === 1 && (
            <ComparisonPage
              next={nextPage}
              onStartTrial={
                isChinaAndroid ? handleChinaAndroidTrial : undefined
              }
              key="comparison"
            />
          )}
          {pageIndex === 2 && !isChinaAndroid && (
            <ReminderStep
              next={nextPage}
              isPremium={serviceLevelStatus.is_premium}
              key="reminder"
            />
          )}
          {pageIndex === 3 && !isChinaAndroid && (
            <TrialPage
              startTrial={trialMutation.mutateAsync}
              onRestore={() => restoreMutation.mutate()}
              key="trial"
            />
          )}
        </AnimatePresence>
      </Layout>
      <SubscribedView isOpen={isSubscribedViewOpen} />
      <LoadingOverlay
        isOpen={
          chinaMutation.isPending ||
          trialMutation.isPending ||
          restoreMutation.isPending
        }
      />
      <GrantPermissionDialog />
      <Dialog />
    </>
  )
}

const checkShouldShowFeaturesPage = ({
  isProUser,
}: {
  isProUser: boolean
}): boolean => {
  if (isProUser) {
    return (
      enterFrom !== 'time_guard_edit_plan' &&
      enterFrom !== 'time_guard_select_apps' &&
      enterFrom !== 'focus_challenge'
    )
  }
  switch (enterFrom) {
    case 'time_guard_edit_plan':
    case 'time_guard_select_apps':
    case 'pause_cover':
    case 'planting_pause':
    case 'statistics':
    case 'tag_list_edit':
    case 'tag_list_create':
    case 'focus_settings_create_tag':
    case 'focus_settings_favorites':
    case 'settings_custom_phrase':
    // focus-challenge lands on the feature-list page (MainPage), not the Rive
    // feature animation, so its prepended daily-challenge bonus is shown.
    case 'focus_challenge':
      return false
    default:
      return true
  }
}
